import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/base/MangaCard";
import { categories, artStyles, mangaList } from "@/mocks/manga";

const sweetnessLevels = [
  { label: "全部甜度", value: "all" },
  { label: "微甜 3+", value: "3" },
  { label: "中甜 4+", value: "4" },
  { label: "爆甜 4.5+", value: "4.5" },
];

const sortOptions = [
  { label: "综合推荐", value: "hot" },
  { label: "甜度最高", value: "sweet" },
  { label: "最新更新", value: "new" },
  { label: "评分最高", value: "rating" },
];

function Chip({ active, onClick, children }: { active?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
        active ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:ring-primary-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function List() {
  const [params, setParams] = useSearchParams();

  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const art = params.get("art") || "";
  const sweet = params.get("sweet") || "all";
  const sort = params.get("sort") || "hot";

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const results = useMemo(() => {
    let list = [...mangaList];
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (m) =>
          m.title.toLowerCase().includes(kw) ||
          m.author.toLowerCase().includes(kw) ||
          m.category.includes(kw) ||
          m.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    if (cat) list = list.filter((m) => m.category === cat);
    if (art) list = list.filter((m) => m.artStyle === art);
    if (sweet !== "all") list = list.filter((m) => m.sweetness >= Number(sweet));
    switch (sort) {
      case "sweet":
        list.sort((a, b) => b.sweetness - a.sweetness);
        break;
      case "new":
        list.sort((a, b) => b.chapters - a.chapters);
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      default:
        list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [q, cat, art, sweet, sort]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "布丁漫画网漫画库",
    url: "https://stripvault.github.io/list",
    description: "按甜度、分类、画风筛选甜系恋爱漫画，甜宠、恋爱、高甜、少女、纯爱、狗粮漫画一网打尽。",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: results.map((m, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: m.title,
        url: `https://stripvault.github.io/manga/${m.id}`,
      })),
    },
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

        {/* Page head */}
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">漫画库</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">漫画库</h1>
            <p className="mt-2 text-sm text-foreground-500">
              共收录 {mangaList.length} 部甜系漫画 · 按甜度 / CP / 画风筛选
              {cat && <> · 当前分类：<strong className="text-primary-600">{cat}</strong></>}
            </p>
          </div>
        </section>

        {/* Filter bar */}
        <section className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6">
          <div className="flex flex-col gap-4 rounded-2xl bg-background-50 p-4 ring-1 ring-background-200 md:p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">分类</span>
              <div className="flex flex-wrap gap-2">
                <Chip active={!cat} onClick={() => update("cat", "")}>全部</Chip>
                {categories.map((c) => (
                  <Chip key={c.name} active={cat === c.name} onClick={() => update("cat", cat === c.name ? "" : c.name)}>
                    {c.name}
                  </Chip>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">甜度</span>
              <div className="flex flex-wrap gap-2">
                {sweetnessLevels.map((s) => (
                  <Chip key={s.value} active={sweet === s.value} onClick={() => update("sweet", s.value)}>
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="w-16 shrink-0 text-xs font-medium text-foreground-500">画风</span>
              <div className="flex flex-wrap gap-2">
                <Chip active={!art} onClick={() => update("art", "")}>全部</Chip>
                {artStyles.map((s) => (
                  <Chip key={s} active={art === s} onClick={() => update("art", art === s ? "" : s)}>
                    {s}
                  </Chip>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-background-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-foreground-500">排序</span>
                {sortOptions.map((s) => (
                  <Chip key={s.value} active={sort === s.value} onClick={() => update("sort", s.value)}>
                    {s.label}
                  </Chip>
                ))}
              </div>
              <span className="text-xs text-foreground-500">
                {q && <>“{q}”的搜索结果 · </>}找到 <strong className="text-primary-600">{results.length}</strong> 部
              </span>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 md:px-6">
          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {results.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl bg-background-100/70 py-16 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-4 text-base font-medium text-foreground-800">没有找到符合条件的漫画</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词或筛选条件试试吧</p>
              <button
                onClick={() => setParams({}, { replace: true })}
                className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600"
              >
                清空筛选
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}